import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { fetchAllRows } from '@/lib/supabaseHelpers';

interface Session {
  total_duration_sec: number | null;
  screen_times: Record<string, number> | null;
  is_completed: boolean | null;
}

const LiveTimingMetrics = () => {
  const [sessions, setSessions] = useState<Session[]>([]);

  const fetchData = async () => {
    const data = await fetchAllRows<Session>('quiz_sessions', 'total_duration_sec, screen_times, is_completed', [
      { column: 'utm_source', operator: 'not.is', value: null },
    ]);
    setSessions(data);
  };

  useEffect(() => {
    fetchData();
    const channel = supabase
      .channel('live-timing')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_sessions' }, () => fetchData())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const completedSessions = sessions.filter(s => s.is_completed && (s.total_duration_sec ?? 0) > 0);
  const avgDuration = completedSessions.length > 0
    ? Math.round(completedSessions.reduce((sum, s) => sum + (s.total_duration_sec ?? 0), 0) / completedSessions.length)
    : 0;

  // Aggregate screen times
  const screenTimeTotals: Record<string, { total: number; count: number }> = {};
  sessions.forEach(s => {
    if (s.screen_times && typeof s.screen_times === 'object') {
      Object.entries(s.screen_times).forEach(([screen, time]) => {
        if (!screenTimeTotals[screen]) screenTimeTotals[screen] = { total: 0, count: 0 };
        screenTimeTotals[screen].total += Number(time) || 0;
        screenTimeTotals[screen].count++;
      });
    }
  });

  const screenTimeData = Object.entries(screenTimeTotals)
    .map(([name, { total, count }]) => ({
      name: `S${(parseInt(name.replace('screen_', '')) + 1)}`,
      seconds: Math.round(total / count),
    }))
    .sort((a, b) => parseInt(a.name.substring(1)) - parseInt(b.name.substring(1)));

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
        <Clock className="w-4 h-4" />
        Timing Metrics
        <span className="ml-auto flex items-center gap-1.5 text-xs font-medium text-emerald-600">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          LIVE
        </span>
      </h3>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-gray-50 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-gray-900">{Math.floor(avgDuration / 60)}m {avgDuration % 60}s</p>
          <p className="text-xs text-gray-500">Gem. doorlooptijd (voltooid)</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-gray-900">{completedSessions.length}</p>
          <p className="text-xs text-gray-500">Voltooide sessies</p>
        </div>
      </div>

      {screenTimeData.length > 0 && (
        <>
          <p className="text-xs text-gray-500 mb-2 font-medium">Gem. tijd per scherm (sec)</p>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={screenTimeData}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(val: number) => [`${val}s`, 'Gem. tijd']} />
              <Bar dataKey="seconds" fill="#2563eb" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
};

export default LiveTimingMetrics;
